import Vue from 'vue'
import gql from 'graphql-tag'
import { mixins } from 'vue-class-component'
import { Component, Watch } from 'vue-property-decorator'

import { Breadcrumb } from '@/interfaces/breadcrumbs'
import AppDialog from '@/components/AppDialog.vue'
import AppBreadcrumbs from '@/components/AppBreadcrumbs.vue'
import { Policy, PolicyStatus } from '@/modules/Policy/interfaces/policy'
import { Account } from '@/modules/Account/interfaces/account'
import { Case } from '@/modules/Case/interfaces/case'
import { Task } from '@/modules/Task/interfaces/task'
import AppGlobalSearch from '@/components/AppGlobalSearch.vue'
import { getDashboards, getExpiringPolicies } from '@/modules/Dashboard/gql/dashboard.gql'
import { ApolloResponse } from '@/interfaces/apollo'
import { showErrors } from '@/utils/snack'
import PoliciesDialog from '@/modules/Dashboard/components/policies-dialog.vue'
import CasesDialog from '@/modules/Dashboard/components/cases-dialog.vue'
import AccountsDialog from '@/modules/Dashboard/components/accounts-dialog.vue'
import TasksDialog from '@/modules/Dashboard/components/tasks-dialog.vue'

@Component({
  components: {
    AppDialog,
    AppBreadcrumbs,
    PoliciesDialog,
    CasesDialog,
    AccountsDialog,
    AppGlobalSearch,
    TasksDialog
  }
})
export default class Dashboard extends Vue {
  breadcrumbs: Breadcrumb[] = [
    {
      text: 'DashBoard'
    }
  ]

  policiesToday: Policy[] = []
  policiesYesterday: Policy[] = []
  policiesToExpire: Policy[] = []
  accountsToday: Account[] = []
  accountsYesterday: Account[] = []
  casesToHandle: Case[] = []
  casesNewBusiness: Case[] = []
  casesRenewal: Case[] = []
  casesRewrite: Case[] = []
  casesRemarket: Case[] = []
  casesCancellation: Case[] = []
  casesEndorsement: Case[] = []
  casesBOR: Case[] = []
  casesCOI: Case[] = []
  accountsNeedAttention: Account[] = []
  tasksOngoing: Task[] = []
  tasksDue: Task[] = []

  pDialog = {
    show: false,
    title: ``,
    list: [] as Policy[]
  }

  cDialog = {
    show: false,
    title: ``,
    highlightTitle: ``,
    list: [] as Case[]
  }

  aDialog = {
    show: false,
    title: ``,
    list: [] as Account[]
  }

  tDialog = {
    show: false,
    title: ``,
    list: [] as Task[]
  }

  get policiesTodayNum () {
    return this.policiesToday.length
  }
  get policiesYesterdayNum () {
    return this.policiesYesterday.length
  }
  get policiesToExpireNum () {
    return this.policiesToExpire.length
  }
  get accountsTodayNum () {
    return this.accountsToday.length
  }
  get accountsYesterdayNum () {
    return this.accountsYesterday.length
  }
  get casesToHandleNum () {
    return this.casesToHandle.length
  }
  get casesNewBusinessNum () {
    return this.casesNewBusiness.length
  }
  get casesRenewalNum () {
    return this.casesRenewal.length
  }
  get casesRewriteNum () {
    return this.casesRewrite.length
  }
  get casesRemarketNum () {
    return this.casesRemarket.length
  }
  get casesCancellationNum () {
    return this.casesCancellation.length
  }
  get casesEndorsementNum () {
    return this.casesEndorsement.length
  }
  get casesBORNum () {
    return this.casesBOR.length
  }
  get casesCOINum () {
    return this.casesCOI.length
  }
  get accountsNeedAttentionNum () {
    return this.accountsNeedAttention.length
  }
  get tasksOngoingNum () {
    return this.tasksOngoing.length
  }
  get tasksDueNum () {
    return this.tasksDue.length
  }

  mounted () {
    this.getDashboardItems()
  }

  async getDashboardItems () {
    try {
      const res: ApolloResponse = await this.$apollo.query({
        query: getDashboards
      })

      if (!res.data) {
        showErrors(res.errors)
        return
      }
      const {
        policiesToday, policiesYesterday,
        policiesToExpire, accountsToday, accountsYesterday,
        casesToHandle, casesNewBusiness, casesRenewal,
        casesRewrite, casesRemarket, casesCancellation,
        casesEndorsement, casesBOR, casesCOI,
        accountsNeedAttention, ongoingTasks, dueInOneWeekTasks
      } = res.data.getDashboardItems

      this.policiesToday = policiesToday
      this.policiesYesterday = policiesYesterday
      this.policiesToExpire = policiesToExpire
      this.accountsToday = accountsToday
      this.accountsYesterday = accountsYesterday
      this.casesToHandle = casesToHandle
      this.casesNewBusiness = casesNewBusiness
      this.casesRenewal = casesRenewal
      this.casesRewrite = casesRewrite
      this.casesRemarket = casesRemarket
      this.casesCancellation = casesCancellation
      this.casesEndorsement = casesEndorsement
      this.casesBOR = casesBOR
      this.casesCOI = casesCOI
      this.accountsNeedAttention = accountsNeedAttention
      this.tasksOngoing = ongoingTasks
      this.tasksDue = dueInOneWeekTasks
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  openDialog (index: number) {
    switch (index) {
      case 0:
        this.pDialog = {
          show: true,
          title: `Today's new policies`,
          list: this.policiesToday
        }
        break
      case 1:
        this.pDialog = {
          show: true,
          title: `Yesterday's new policies`,
          list: this.policiesYesterday
        }
        break
      case 2:
        this.pDialog = {
          show: true,
          title: `Expiring policies`,
          list: this.policiesToExpire
        }
        break
      case 3:
        this.aDialog = {
          show: true,
          title: `Today's new accounts`,
          list: this.accountsToday
        }
        break
      case 4:
        this.aDialog = {
          show: true,
          title: `Yesterday's new accounts`,
          list: this.accountsYesterday
        }
        break
      case 5:
        this.cDialog = {
          show: true,
          title: `Total open Cases`,
          highlightTitle: 'All Types',
          list: this.casesToHandle
        }
        break
      case 6:
        this.cDialog = {
          show: true,
          title: `Total open Cases`,
          highlightTitle: '(New Business)',
          list: this.casesNewBusiness
        }
        break
      case 7:
        this.cDialog = {
          show: true,
          title: `Total open Cases`,
          highlightTitle: '(Renewal)',
          list: this.casesRenewal
        }
        break
      case 8:
        this.cDialog = {
          show: true,
          title: `Total open Cases`,
          highlightTitle: '(Rewrite)',
          list: this.casesRewrite
        }
        break
      case 9:
        this.cDialog = {
          show: true,
          title: `Total open Cases`,
          highlightTitle: '(Remarket)',
          list: this.casesRemarket
        }
        break
      case 10:
        this.cDialog = {
          show: true,
          title: `Total open Cases`,
          highlightTitle: '(Cancellation)',
          list: this.casesCancellation
        }
        break
      case 11:
        this.cDialog = {
          show: true,
          title: `Total open Cases`,
          highlightTitle: '(Endorsement)',
          list: this.casesEndorsement
        }
        break
      case 12:
        this.cDialog = {
          show: true,
          title: `Total open Cases`,
          highlightTitle: '(BOR)',
          list: this.casesBOR
        }
        break
      case 13:
        this.cDialog = {
          show: true,
          title: `Total open Cases`,
          highlightTitle: '(COI)',
          list: this.casesCOI
        }
        break
      case 14:
        this.aDialog = {
          show: true,
          title: `Customers with open cases`,
          list: this.accountsNeedAttention
        }
        break
      case 15:
        this.tDialog = {
          show: true,
          title: `Ongoing tasks`,
          list: this.tasksOngoing
        }
        break
      case 16:
        this.tDialog = {
          show: true,
          title: `Due in 7 days tasks`,
          list: this.tasksDue
        }
        break
    }
  }

  handleCancel () {
    this.cDialog.show = false
    this.aDialog.show = false
    this.pDialog.show = false
    this.tDialog.show = false
  }
}
